import React, { useEffect, useState, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { 
  Container, 
  Typography, 
  Box, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Button, 
  Card, 
  CardContent, 
  Avatar, 
  Tabs, 
  Tab,
  Grid,
  Paper,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Fade,
  useTheme,
  alpha
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import BookIcon from '@mui/icons-material/Book';
import TimerIcon from '@mui/icons-material/Timer';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SchoolIcon from '@mui/icons-material/School';
import StarIcon from '@mui/icons-material/Star';
import SpeedIcon from '@mui/icons-material/Speed';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Fade in={value === index} timeout={300}>
          <Box sx={{ py: 4 }}>
            {children}
          </Box>
        </Fade>
      )}
    </div>
  );
}

// 과목별 아이콘 매핑
const getSubjectIcon = (subjectName) => {
  const iconMap = {
    'Civil Procedure': '⚖️',
    'Constitutional Law': '🏛️',
    'Contracts': '📋',
    'Criminal Law': '🔒',
    'Criminal Procedure': '👮‍♂️',
    'Torts': '⚡',
    'Evidence': '🔍',
    'Property': '🏠',
    'Criminal Law & Procedure': '🚔'
  };
  return iconMap[subjectName] || '📚';
};

// 과목별 색상 매핑
const getSubjectColor = (subjectName) => {
  const colorMap = {
    'Civil Procedure': '#1976d2',
    'Constitutional Law': '#d32f2f',
    'Contracts': '#388e3c',
    'Criminal Law': '#f57c00',
    'Criminal Procedure': '#7b1fa2',
    'Torts': '#c62828',
    'Evidence': '#0288d1',
    'Property': '#689f38',
    'Criminal Law & Procedure': '#5d4037'
  };
  return colorMap[subjectName] || '#424242';
};

export default function MbeTestMainPage() {
  const { user, isEvaluator, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  const [sets, setSets] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedSetId, setSelectedSetId] = useState('');

  // 표시할 MBE 과목 목록
  const mbeSubjects = [
    'Civil Procedure',
    'Constitutional Law',
    'Contracts',
    'Criminal Law',
    'Criminal Procedure',
    'Torts',
    'Evidence',
    'Property'
  ];

  useEffect(() => {
    const checkAuthorization = async () => {
      if (authLoading) return;
      if (isEvaluator) {
        setIsAuthorized(true);
        setAuthChecked(true);
        return;
      }
      if (!user) {
        setIsAuthorized(false);
        setAuthChecked(true);
        return;
      }
      const { data } = await supabase
        .from('mbe_authorized_users')
        .select('user_id')
        .eq('user_id', user.id)
        .single();
      
      setIsAuthorized(!!data);
      setAuthChecked(true);
    };

    checkAuthorization();
  }, [user, isEvaluator, authLoading]);

  useEffect(() => {
    if (!isAuthorized) return;

    const fetchInitialData = async () => {
      const { data: setsData } = await supabase
        .from('mbe_sets')
        .select('id, title, exam_year, exam_month')
        .order('exam_year', { ascending: false });
      if (setsData) setSets(setsData);

      const { data: subjectsData } = await supabase
        .from('subjects')
        .select('*')
        .in('name', mbeSubjects)
        .order('name', { ascending: true });
      if (subjectsData) {
        setSubjects(subjectsData);
      }
    };

    fetchInitialData();
  }, [isAuthorized]);

  const displaySubjects = useMemo(() => {
    const otherSubjects = subjects.filter(
      s => s.name !== 'Criminal Law' && s.name !== 'Criminal Procedure'
    );

    const crimLaw = subjects.find(s => s.name === 'Criminal Law');
    const crimPro = subjects.find(s => s.name === 'Criminal Procedure');

    const combined = [...otherSubjects];

    if (crimLaw && crimPro) {
      combined.push({
        id: `${crimLaw.id},${crimPro.id}`,
        name: 'Criminal Law & Procedure',
      });
    } else {
      if (crimLaw) combined.push(crimLaw);
      if (crimPro) combined.push(crimPro);
    }
    
    return combined.sort((a, b) => a.name.localeCompare(b.name));
  }, [subjects]);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };
  
  const startTestMode = () => {
    if (!selectedSetId) return;
    navigate(`/mbe_test/test?mode=test&setId=${selectedSetId}`);
  };

  const startStudyMode = (subjectId) => {
    navigate(`/mbe_test/test?mode=study&subjectId=${subjectId}`);
  };

  if (!authChecked) {
    return (
      <Container maxWidth="md" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <LinearProgress sx={{ width: 200, mb: 2 }} />
          <Typography variant="h6" color="text.secondary">로딩 중...</Typography>
        </Box>
      </Container>
    );
  }

  if (!isAuthorized) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: 'calc(100vh - 200px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            py: 4,
          }}
        >
          <Card sx={{ 
            width: '100%', 
            p: { xs: 3, sm: 5 }, 
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`
          }}>
            <CardContent sx={{ p: 0 }}>
              <Avatar sx={{ 
                m: 'auto', 
                bgcolor: 'primary.main', 
                width: 80, 
                height: 80,
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
              }}>
                <LockOutlinedIcon sx={{ fontSize: '2.5rem' }} />
              </Avatar>
              <Typography component="h1" variant="h4" sx={{ 
                mt: 3, 
                mb: 2, 
                fontWeight: 700,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                접근 권한 필요
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 4, lineHeight: 1.8, fontSize: '1.1rem' }}>
                MBE 실전 테스트는 승인된 사용자만 이용할 수 있는
                <br />
                <strong>프리미엄 학습 기능</strong>입니다.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/')}
                sx={{ 
                  px: 6, 
                  py: 2, 
                  borderRadius: '50px', 
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.25)'
                  }
                }}
              >
                홈으로 돌아가기
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`
    }}>
      <Container maxWidth="xl" sx={{ py: 6 }}>
        {/* 헤더 섹션 */}
        <Box sx={{ 
          textAlign: 'center', 
          mb: 6,
          py: 4,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
          borderRadius: 4,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="h2" sx={{ 
              fontWeight: 800, 
              mb: 2,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              MBE 실전 테스트
            </Typography>
            <Typography variant="h5" color="text.secondary" sx={{ mb: 3, fontWeight: 300 }}>
              Professional Bar Exam Preparation Platform
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
              <Chip 
                icon={<StarIcon />} 
                label="실전 시뮬레이션" 
                color="primary" 
                variant="outlined"
                sx={{ fontSize: '1rem', py: 1 }}
              />
              <Chip 
                icon={<SpeedIcon />} 
                label="즉시 피드백" 
                color="secondary" 
                variant="outlined"
                sx={{ fontSize: '1rem', py: 1 }}
              />
              <Chip 
                icon={<TrendingUpIcon />} 
                label="성과 분석" 
                color="success" 
                variant="outlined"
                sx={{ fontSize: '1rem', py: 1 }}
              />
            </Box>
          </Box>
          {isEvaluator && (
            <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 10 }}>
              <Tooltip title="문제 관리 페이지로 이동" placement="left">
                <Button 
                  variant="contained" 
                  color="secondary" 
                  onClick={() => navigate('/mbe_admin')}
                  sx={{ 
                    borderRadius: '50px', 
                    fontWeight: 'bold',
                    minWidth: '120px',
                    height: '40px',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                    }
                  }}
                >
                  문제 관리
                </Button>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* 탭 네비게이션 */}
        <Paper sx={{ 
          mb: 4, 
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }}>
          <Tabs 
            value={tabIndex} 
            onChange={handleTabChange} 
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                py: 3,
                fontSize: '1.1rem',
                fontWeight: 600,
                textTransform: 'none'
              }
            }}
          >
            <Tab 
              icon={<TimerIcon />} 
              iconPosition="start"
              label="실전 시험 모드" 
              id="tab-0" 
            />
            <Tab 
              icon={<BookIcon />} 
              iconPosition="start"
              label="과목별 학습 모드" 
              id="tab-1" 
            />
          </Tabs>
        </Paper>
        
        {/* 실전 시험 모드 */}
        <TabPanel value={tabIndex} index={0}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Paper sx={{ 
                p: 4, 
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                height: 'fit-content'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <AssignmentIcon sx={{ fontSize: '2rem', mr: 2, color: 'primary.main' }} />
                  <Typography variant="h4" fontWeight={700}>
                    실전 시험 시작
                  </Typography>
                </Box>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.7 }}>
                  실제 시험과 동일한 환경에서 문제를 풀어보세요. 시간 제한과 채점 기능이 포함되어 있습니다.
                </Typography>
                
                <FormControl fullWidth sx={{ mb: 4 }}>
                  <InputLabel sx={{ fontSize: '1.1rem' }}>시험 세트 선택</InputLabel>
                  <Select
                    value={selectedSetId}
                    label="시험 세트 선택"
                    onChange={e => setSelectedSetId(e.target.value)}
                    sx={{ 
                      '& .MuiSelect-select': { 
                        py: 2,
                        fontSize: '1.1rem'
                      }
                    }}
                  >
                    {sets.map(set => (
                      <MenuItem key={set.id} value={set.id} sx={{ py: 2 }}>
                        <Box>
                          <Typography variant="body1" fontWeight={600}>
                            {set.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {set.exam_year}년 {set.exam_month}월 시험
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <Button
                  variant="contained"
                  size="large"
                  disabled={!selectedSetId}
                  onClick={startTestMode}
                  startIcon={<PlayArrowIcon />}
                  sx={{ 
                    py: 2, 
                    px: 4,
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    borderRadius: '50px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.25)'
                    }
                  }}
                >
                  시험 시작하기
                </Button>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Paper sx={{ 
                p: 3, 
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`
              }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                  💡 시험 모드 특징
                </Typography>
                <Box sx={{ space: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TimerIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="body2">실제 시험 시간 제한</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TrendingUpIcon sx={{ mr: 1, color: 'success.main' }} />
                    <Typography variant="body2">즉시 채점 및 분석</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SchoolIcon sx={{ mr: 1, color: 'secondary.main' }} />
                    <Typography variant="body2">상세한 해설 제공</Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        {/* 과목별 학습 모드 */}
        <TabPanel value={tabIndex} index={1}>
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
              과목별 심화 학습
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', lineHeight: 1.7 }}>
              각 과목별로 체계적인 학습을 진행하세요. 개념 이해부터 실전 문제 해결까지 단계별로 학습할 수 있습니다.
            </Typography>
          </Box>
          
          <Grid container spacing={3}>
            {displaySubjects.map((subject, index) => (
              <Grid item xs={12} sm={6} lg={4} key={subject.id}>
                <Fade in timeout={300 + index * 100}>
                  <Card sx={{ 
                    height: '100%',
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
                    }
                  }}
                  onClick={() => startStudyMode(subject.id)}
                  >
                    <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        mb: 2,
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: alpha(getSubjectColor(subject.name), 0.1)
                      }}>
                        <Typography sx={{ fontSize: '2rem', mr: 2 }}>
                          {getSubjectIcon(subject.name)}
                        </Typography>
                        <Typography 
                          variant="h6" 
                          fontWeight={700}
                          sx={{ 
                            color: getSubjectColor(subject.name),
                            flexGrow: 1
                          }}
                        >
                          {subject.name}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, flexGrow: 1 }}>
                        {subject.name}에 대한 핵심 개념과 실전 문제를 통해 체계적으로 학습하세요.
                      </Typography>
                      
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<PlayArrowIcon />}
                        sx={{ 
                          py: 1.5,
                          fontWeight: 'bold',
                          borderRadius: '50px',
                          backgroundColor: getSubjectColor(subject.name),
                          '&:hover': {
                            backgroundColor: alpha(getSubjectColor(subject.name), 0.8)
                          }
                        }}
                      >
                        학습 시작
                      </Button>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </TabPanel>
      </Container>
    </Box>
  );
}
